/**
 * Weight unit helpers. Weights are stored internally in kilograms; these
 * helpers convert to/from the unit shown in the UI.
 */
export type WeightUnit = 'kg' | 't';

export const weightUnits: WeightUnit[] = ['kg', 't'];

interface WeightUnitInfo {
  symbol: string;
  /** Kilograms per one unit. */
  factor: number;
  /** Fraction digits shown for this unit. */
  maxFractionDigits: number;
}

const weightUnitInfo: Record<WeightUnit, WeightUnitInfo> = {
  kg: { symbol: 'kg', factor: 1, maxFractionDigits: 1 },
  t: { symbol: 't', factor: 1000, maxFractionDigits: 3 },
};

function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

export function weightUnitSymbol(unit: WeightUnit): string {
  return weightUnitInfo[unit].symbol;
}

export function weightUnitFractionDigits(unit: WeightUnit): number {
  return weightUnitInfo[unit].maxFractionDigits;
}

/** Converts a kilogram value into the given display unit. */
export function toDisplayWeight(kilograms: number, unit: WeightUnit): number {
  const info = weightUnitInfo[unit];
  return round(kilograms / info.factor, info.maxFractionDigits);
}

/** Converts a value entered in the given unit back to kilograms. */
export function fromDisplayWeight(value: number, unit: WeightUnit): number {
  return round(value * weightUnitInfo[unit].factor, 3);
}

/** Formats a kilogram value as a string in the given unit including its symbol. */
export function formatWeight(kilograms: number, unit: WeightUnit): string {
  return `${toDisplayWeight(kilograms, unit)} ${weightUnitInfo[unit].symbol}`;
}
